/**
 * Lightweight, network-free check for "is someone signed in on this device?".
 *
 * Public marketing pages deliberately never call `useAuth()` (so they issue
 * zero backend requests). But showing a bare "SIGN IN" button to an already
 * signed-in user makes the app look like it logged them out. This reads the
 * persisted auth token from localStorage only — no Supabase engagement, no
 * network, and it is never used for authorization (RLS remains authoritative).
 */
import { useEffect, useState } from "react";

function hasStoredSession(): boolean {
  try {
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (!key || !/^sb-.*-auth-token$/.test(key)) continue;
      const raw = localStorage.getItem(key);
      if (!raw) continue;
      const parsed = JSON.parse(raw) as { expires_at?: number; refresh_token?: string };
      // A refresh token means the session can be restored even if the access
      // token has expired, which is exactly what the app does on load.
      if (parsed?.refresh_token) return true;
      if (typeof parsed?.expires_at === "number" && parsed.expires_at * 1000 > Date.now()) return true;
    }
  } catch {
    return false;
  }
  return false;
}

/** true once mounted if this device has a stored session. SSR-safe (false first render). */
export function useSignedInHint(): boolean {
  const [signedIn, setSignedIn] = useState(false);
  useEffect(() => { setSignedIn(hasStoredSession()); }, []);
  return signedIn;
}
